import { on as onEvent } from "common/utils/ui/event";

const SECTION_LINK_SELECTOR = ".mobile-menu__section";
const HIDDEN_CLASSNAME = "hidden";

const selectCategory = ({
  category,
  $getNavigationContent,
  $getNavigationLinks,
  activeLinkClassName
}) => {
  const activeCategory = category || Array.from($getNavigationContent())[0]?.dataset?.category;

  $getNavigationLinks().forEach(navLink => {
    navLink.classList.toggle(activeLinkClassName, navLink.dataset.category === activeCategory);
  });

  $getNavigationContent().forEach(content => {
    content.classList.toggle(HIDDEN_CLASSNAME, content.dataset.category !== activeCategory);
  });
};

const handleSlideToggle = ({
  event,
  slideSelector,
  expandedSlideClassName,
  mobileBackMode,
  $getContainer,
  $getSlides
}) => {
  const $slide = event.target.closest(slideSelector);

  if (!$slide) return;

  $slide.classList.toggle(expandedSlideClassName);

  const $container = $getContainer();
  const $section = $container?.closest(SECTION_LINK_SELECTOR);

  if (!$container || !$section) return;

  const { height } = $container.getBoundingClientRect();

  if (mobileBackMode === "reset-all") {
    const extendedSlides = Array.from($getSlides()).some($el =>
      $el.classList.contains(expandedSlideClassName)
    );
    $section.style.minHeight = extendedSlides ? `${height}px` : "0";
  } else {
    const minHeight = $slide.classList.contains(expandedSlideClassName) ? height : 0;
    $section.style.minHeight = `${minHeight}px`;
  }
};

const handleLevelBasedBack = ({
  event,
  backSelector,
  slideSelector,
  expandedSlideClassName,
  $getContainer
}) => {
  const $backButton = event.target.closest(backSelector);
  const currentLevel = parseInt($backButton.dataset.level, 10);
  const $currentSlide = $backButton.closest(slideSelector);

  if (!$currentSlide) return;

  if (currentLevel === 3) {
    const $parentSlide = $currentSlide.parentElement.closest(slideSelector);
    $currentSlide.classList.remove(expandedSlideClassName);
    $parentSlide?.classList.add(expandedSlideClassName);
  } else if (currentLevel === 2) {
    $currentSlide.classList.remove(expandedSlideClassName);
  }

  const $section = $backButton.closest(SECTION_LINK_SELECTOR);
  if ($section) {
    const $container = $getContainer();
    if ($container) {
      const { height } = $container.getBoundingClientRect();
      $section.style.minHeight = `${height}px`;
    }
  }
};

const buildDesktopNavigation =
  ({
    hasDesktopNav,
    onDesktopInit,
    mouseleaveCategory,
    select,
    $getNavigationLinks,
    $getRootNavigation
  }) =>
  () => {
    if (!hasDesktopNav) return;

    $getNavigationLinks().forEach($link => {
      const listener = () => select($link.dataset.category);
      $link.addEventListener("click", listener);
      $link.addEventListener("mouseover", listener);
    });

    $getRootNavigation().forEach($link => {
      $link.addEventListener("mouseleave", () => {
        if ($link.dataset.category === mouseleaveCategory) select();
      });
    });

    if (onDesktopInit) onDesktopInit();
  };

const buildMobileNavigation =
  ({
    slideLinkSelector,
    slideSelector,
    expandedSlideClassName,
    mobileBackMode,
    backSelector,
    $getContainer,
    $getSlides
  }) =>
  () => {
    onEvent("click", slideLinkSelector, event => {
      event.preventDefault();
      handleSlideToggle({
        event,
        slideSelector,
        expandedSlideClassName,
        mobileBackMode,
        $getContainer,
        $getSlides
      });
    });

    if (mobileBackMode === "level-based") {
      onEvent("click", backSelector, event => {
        handleLevelBasedBack({
          event,
          backSelector,
          slideSelector,
          expandedSlideClassName,
          $getContainer
        });
      });
    } else {
      onEvent("click", backSelector, event => {
        const $section = event.target.closest(SECTION_LINK_SELECTOR);
        if ($section) $section.style.minHeight = "0";
        $getSlides().forEach($slide => $slide.classList.remove(expandedSlideClassName));
      });
    }
  };

const createNavigation = ({
  containerSelector,
  navigationLinkSelector,
  navigationContentSelector,
  activeLinkClassName,
  slideSelector,
  slideLinkSelector,
  expandedSlideClassName,
  backSelector,
  mouseleaveCategory,
  mobileBackMode,
  hasDesktopNav = true,
  onDesktopInit = null
}) => {
  const $getContainer = () => document.querySelector(containerSelector);
  const $getRootNavigation = () => document.querySelectorAll(".js-masthead-subsite-navigation");
  const $getNavigationLinks = () => document.querySelectorAll(navigationLinkSelector);
  const $getNavigationContent = () => document.querySelectorAll(navigationContentSelector);
  const $getSlides = () => document.querySelectorAll(slideSelector);

  const select = category =>
    selectCategory({ category, $getNavigationContent, $getNavigationLinks, activeLinkClassName });

  const initDesktopNavigation = buildDesktopNavigation({
    hasDesktopNav,
    onDesktopInit,
    mouseleaveCategory,
    select,
    $getNavigationLinks,
    $getRootNavigation
  });

  const initMobileNavigation = buildMobileNavigation({
    slideLinkSelector,
    slideSelector,
    expandedSlideClassName,
    mobileBackMode,
    backSelector,
    $getContainer,
    $getSlides
  });

  return { initDesktopNavigation, initMobileNavigation };
};

export default createNavigation;
